import React, { createContext, useContext, useState, useEffect } from 'react';
import voterService from '../api/voterService';

const CountiesContext = createContext();

export const useCounties = () => {
  const context = useContext(CountiesContext);
  if (!context) {
    throw new Error('useCounties must be used within a CountiesProvider');
  }
  return context;
};

export const CountiesProvider = ({ children }) => {
  const [allCounties, setAllCounties] = useState([]);
  const [countiesByState, setCountiesByState] = useState({});
  const [availableStates, setAvailableStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all counties once when app loads
  useEffect(() => {
    fetchAllCounties();
  }, []);

  const fetchAllCounties = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await voterService.getCounties();
      
      if (data.success) {
        const countiesData = data.data || [];
        
        // Store all counties
        setAllCounties(countiesData);
        
        // Extract all unique states first (including those with no counties)
        const statesFromAPI = [...new Set(countiesData
          .map(item => item.state)
          .filter(state => state && state.trim() !== '' && state !== 'null')
          .map(state => state.toUpperCase())
        )];
        
        // Ensure AR is included even if not in API data
        const requiredStates = ['AR', 'FL', 'NC', 'OH', 'OK', 'PA'];
        const allStatesFromData = [...new Set([...statesFromAPI, ...requiredStates])].sort();
        
        
        // Group counties by state for faster lookups
        const groupedByState = {};
        
        // Initialize all states (even those with no counties)
        allStatesFromData.forEach(state => {
          groupedByState[state] = [];
        });
        
        // Add counties to their respective states
        countiesData.forEach(item => {
          if (item.state) {
            const stateKey = item.state.toUpperCase();
            if (groupedByState[stateKey] && item.county) {
              groupedByState[stateKey].push(item.county);
            }
            // Note: States with null counties will have empty arrays, which is correct
          }
        });
        
        // Sort counties within each state
        Object.keys(groupedByState).forEach(state => {
          groupedByState[state].sort();
        });
        
        
        setCountiesByState(groupedByState);
        setAvailableStates(allStatesFromData);
        
      } else {
        throw new Error(data.error || 'Failed to fetch counties');
      }
    } catch (error) {
      console.error('Error loading counties:', error);
      setError(error.message || 'Failed to load counties');
      setAllCounties([]);
      setCountiesByState({});
      setAvailableStates([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get counties for a specific state
  const getCountiesForState = (state) => {
    if (!state) return [];
    const stateKey = state.toUpperCase();
    const counties = countiesByState[stateKey] || [];
    return counties;
  };

  // Get all counties with state labels (for when no state is selected)
  const getAllCountiesWithStates = () => {
    const result = allCounties.map(item => ({
      value: item.county,
      label: `${item.county} (${item.state})`,
      state: item.state
    }));
    return result;
  };

  const value = {
    allCounties,
    countiesByState,
    availableStates,
    isLoading,
    error,
    getCountiesForState,
    getAllCountiesWithStates,
    refresh: fetchAllCounties
  };

  return (
    <CountiesContext.Provider value={value}>
      {children}
    </CountiesContext.Provider>
  );
};

export default CountiesContext;